/**
 * Observable de estado de streaming - sincronización en tiempo real
 * Patrón subscribe/emit para notificar cambios de estado.
 * Emitido por: broadcast-live, broadcast-ended, connect, disconnect
 */
import { io } from 'socket.io-client';
import { getSocketUrl } from '../config/socket';

const INITIAL_STATE = {
  isLive: false,
  broadcasterId: null,
  isConnected: false,
};

class StreamingObservable {
  constructor() {
    this.subscribers = new Set();
    this.state = { ...INITIAL_STATE };
    this.socket = null;
    this.watching = false;
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    callback(this.state);
    return () => this.subscribers.delete(callback);
  }

  _emit() {
    this.subscribers.forEach((cb) => cb(this.state));
  }

  _setState(partial) {
    this.state = { ...this.state, ...partial };
    this._emit();
  }

  connect() {
    if (this.socket?.connected) return;
    const url = getSocketUrl();
    this.socket = io(url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      this._setState({ isConnected: true });
      if (this.watching) {
        this.socket.emit('watch-stream');
      }
    });

    this.socket.on('disconnect', () => {
      this._setState({ isConnected: false, isLive: false, broadcasterId: null });
    });

    this.socket.on('broadcast-live', ({ broadcasterId }) => {
      this._setState({ isLive: true, broadcasterId });
    });

    this.socket.on('broadcast-ended', () => {
      this._setState({ isLive: false, broadcasterId: null });
    });
  }

  watch() {
    this.watching = true;
    this.connect();
    if (this.socket?.connected) {
      this.socket.emit('watch-stream');
    }
  }

  unwatch() {
    this.watching = false;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this._setState(INITIAL_STATE);
  }

  getState() {
    return this.state;
  }
}

export const streamingObservable = new StreamingObservable();
export { INITIAL_STATE };
